<script setup lang="ts">
/**
 * Draggable and resizable floating panel for canvas tools.
 *
 * Affordances: a move grip at the top-left of the header, a resize grip pinned to the
 * bottom-right corner. Only the content scrolls — the title bar always stays visible.
 *
 * Geometry is remembered per `panelId` (see stores/FloatingPanelState), so closing
 * and reopening a panel brings it back exactly where the user put it.
 *
 * The panel is always kept fully inside the drawing area: a panel pushed past the
 * edge could no longer be grabbed back.
 */
import { ref, computed, watch, onBeforeUnmount } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_MODAL } from "./layouts/constants";
import {
  getPanelGeometry,
  setPanelGeometry,
  type PanelGeometry,
} from "@/stores/FloatingPanelState";
// A panel dragged past the edge of the drawing area would be unreachable, so every
// geometry change goes through clampToBounds() below.
import { getCanvasBounds as getBounds, clamp } from "./canvasBounds";

const props = defineProps<{
  /** Identifies the panel so its position and size survive close/reopen. */
  panelId: string;
  title: string;
  open: boolean;
}>();

const emit = defineEmits<{ close: [] }>();

/** Default size — panels open at their maximum and scroll inside. */
// Wide enough for the busiest row: a point parameter's name plus its X and Y fields.
const DEFAULT_WIDTH = 340;
const DEFAULT_MAX_HEIGHT = 420;
const MIN_WIDTH = 220;
const MIN_HEIGHT = 140;
/** Keeps the panel clear of the bottom toolbar when it first opens. */
const BOTTOM_MARGIN = 110;

/** Fit a geometry inside the bounds: size first, then position. */
function clampToBounds(
  geo: PanelGeometry,
  bounds = getBounds(),
): PanelGeometry {
  // A panel can never be wider or taller than the area holding it.
  const width = Math.min(Math.max(geo.width, MIN_WIDTH), bounds.width);
  const height = Math.min(Math.max(geo.height, MIN_HEIGHT), bounds.height);

  return {
    width,
    height,
    left: clamp(geo.left, bounds.left, bounds.left + bounds.width - width),
    top: clamp(geo.top, bounds.top, bounds.top + bounds.height - height),
  };
}

const geometry = ref<PanelGeometry>(defaultGeometry());

function defaultGeometry(): PanelGeometry {
  const bounds = getBounds();
  const width = Math.min(DEFAULT_WIDTH, bounds.width);
  const height = Math.min(DEFAULT_MAX_HEIGHT, bounds.height);

  return clampToBounds(
    {
      width,
      height,
      left: bounds.left + (bounds.width - width) / 2,
      // Sits just above the bottom toolbar, where the opening button is.
      top: bounds.top + bounds.height - height - BOTTOM_MARGIN,
    },
    bounds,
  );
}

// Restore the remembered geometry (or compute a default) each time the panel opens.
watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    const remembered = getPanelGeometry(props.panelId);
    // Re-clamp on reopen: the window may have been resized meanwhile.
    geometry.value = remembered ? clampToBounds(remembered) : defaultGeometry();
  },
  { immediate: true },
);

const panelStyle = computed(() => ({
  left: `${geometry.value.left}px`,
  top: `${geometry.value.top}px`,
  width: `${geometry.value.width}px`,
  height: `${geometry.value.height}px`,
  zIndex: Z_INDEX_MODAL,
}));

type DragMode = "move" | "resize";
let dragMode: DragMode | null = null;
let startPointer = { x: 0, y: 0 };
let startGeometry: PanelGeometry | null = null;

function beginDrag(mode: DragMode, event: PointerEvent): void {
  dragMode = mode;
  startPointer = { x: event.clientX, y: event.clientY };
  startGeometry = { ...geometry.value };
  (event.target as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
}

function onPointerMove(event: PointerEvent): void {
  if (!dragMode || !startGeometry) return;
  const dx = event.clientX - startPointer.x;
  const dy = event.clientY - startPointer.y;

  if (dragMode === "move") {
    geometry.value = clampToBounds({
      ...startGeometry,
      left: startGeometry.left + dx,
      top: startGeometry.top + dy,
    });
    return;
  }

  // Resize: the top-left corner stays anchored, so the panel grows towards the
  // bottom-right as the grip is dragged away from it — never past the bounds.
  const bounds = getBounds();
  geometry.value = clampToBounds(
    {
      ...startGeometry,
      width: Math.min(
        startGeometry.width + dx,
        bounds.left + bounds.width - startGeometry.left,
      ),
      height: Math.min(
        startGeometry.height + dy,
        bounds.top + bounds.height - startGeometry.top,
      ),
    },
    bounds,
  );
}

function endDrag(): void {
  if (!dragMode) return;
  dragMode = null;
  startGeometry = null;
  // Remember where the user left it.
  setPanelGeometry(props.panelId, geometry.value);
}

/**
 * Shrinking the window (or rotating a tablet) can leave an untouched panel outside
 * the drawing area, where it could no longer be grabbed — pull it back in.
 */
function handleWindowResize(): void {
  if (!props.open) return;
  geometry.value = clampToBounds(geometry.value);
  setPanelGeometry(props.panelId, geometry.value);
}

window.addEventListener("resize", handleWindowResize);

onBeforeUnmount(() => {
  endDrag();
  window.removeEventListener("resize", handleWindowResize);
});
</script>

<template>
  <Teleport to="body">
    <section
      v-if="open"
      class="floating-panel"
      :style="panelStyle"
      role="dialog"
      :aria-label="title"
    >
      <!-- La géométrie (left/top/width/height) est bornée au canvas en pixels
           écran : seul l'intérieur est zoomé, pas la racine. -->
      <header class="panel-header canvas-text-scaled">
        <PhosphorIcon
          class="grip grip-move"
          name="dots-six"
          :size="18"
          :title="$t('canvas:floating.panel.move')"
          @pointerdown="(e: PointerEvent) => beginDrag('move', e)"
          @pointermove="onPointerMove"
          @pointerup="endDrag"
          @pointercancel="endDrag"
        />

        <h6 class="panel-title">{{ title }}</h6>

        <button
          type="button"
          class="panel-close"
          :title="$t('close')"
          @click="emit('close')"
        >
          <PhosphorIcon name="x" :size="14" />
        </button>
      </header>

      <!-- Only this area scrolls: the header stays put. -->
      <div class="panel-content canvas-text-scaled"><slot /></div>

      <!-- Resize grip pinned to the bottom-right corner, where it is dragged from. -->
      <PhosphorIcon
        class="grip grip-resize"
        name="arrows-out-simple"
        :size="18"
        :title="$t('canvas:floating.panel.resize')"
        @pointerdown="(e: PointerEvent) => beginDrag('resize', e)"
        @pointermove="onPointerMove"
        @pointerup="endDrag"
        @pointercancel="endDrag"
      />
    </section>
  </Teleport>
</template>

<style lang="scss" scoped>
.floating-panel {
  position: fixed;
  display: flex;
  flex-direction: column;
  // The panel is dark whatever the app theme, so the browser must render its
  // native widgets dark too — scrollbar, number spinners, <select> popup. Without
  // it Windows draws its opaque light scrollbar right across the panel (macOS
  // hides the same one behind overlay bars, until a mouse is plugged in).
  color-scheme: dark;
  border-radius: 8px;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  color: white;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--canvas-border);
  // The header is the drag surface: never let a drag select its text.
  user-select: none;
  flex: 0 0 auto;
}

.panel-title {
  flex: 1;
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.85);
}

.grip {
  color: rgba(255, 255, 255, 0.45);
  // touch-action:none is what makes pointer drags work on tablets.
  touch-action: none;

  &:hover {
    color: white;
  }
}

.grip-move {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.grip-resize {
  position: absolute;
  right: 2px;
  bottom: 2px;
  padding: 3px;
  // Sits above the content's scrollbar, which otherwise hides it.
  z-index: 1;
  border-radius: 4px;
  // Opaque backing so the glyph stays readable over the scrollbar and the rows.
  background-color: var(--canvas-bg);
  color: white;
  cursor: nwse-resize;
  // The glyph points top-left/bottom-right once rotated a quarter turn.
  transform: rotate(90deg);

  &:hover {
    background-color: var(--canvas-bg-hover, rgba(255, 255, 255, 0.12));
  }
}

.panel-close {
  display: inline-flex;
  padding: 2px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.45);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.panel-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  // Bottom room so the last row never hides behind the resize grip.
  padding: 8px 0 20px;
  // Slim translucent bar on the platforms that draw a permanent one; a no-op where
  // scrollbars overlay the content (macOS, iPadOS), which reserve no width anyway.
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}
</style>
